import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Grid,
  GridItem,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import { IconCheckmark1 } from 'central-icons/IconCheckmark1';
import { IconPause } from 'central-icons/IconPause';
import { IconPlay } from 'central-icons/IconPlay';
import { isToday } from 'date-fns';
import { isEmpty } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation } from 'urql';

import { useContactSalesMutation, useGetLoggedInUser } from '@revelio/auth';
import {
  CTAInteractionTrackingEvents,
  TutorialTrackingEvents,
  useTourStore,
} from '@revelio/core';
import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';
import { useChartTheme } from '@revelio/replots';
import { Button as UiButton } from '@revelio/ui';

import {
  type VideoData,
  videoMetadata,
} from '../../shared/ui/tutorial/Tutorial';
import { UpdateUserMutation } from '../adminRewrite/userOperations';
import {
  toastContactSalesAlreadyNotified,
  toastContactSalesRequestError,
  toastContactSalesRequestSuccess,
} from '../trialUser/contact-sales-toast';
import { useFreeTrialAlertModalState } from '../trialUser/useFreeTrialAlertModalState';
import modalStyles from './tutorial-modal.module.css';
import {
  buildTutorialModalGroups,
  TutorialModalContent,
} from './tutorial-modal-content';
import { useThemedWistiaPlayerColor } from './use-themed-wistia-player-color';

const themedGroups = buildTutorialModalGroups();

const Tutorial = () => {
  const {
    loggedInUser,
    // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    query: [{ fetching }],
    refetch: refetchUserData,
  } = useGetLoggedInUser();

  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
  const [videos, setVideos] = useState<any>(videoMetadata);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [videoId, setVideoIdState] = useState<string | null>('0');

  const [videoPlaying, setVideoPlaying] = useState(false);

  const gridItemRef = useRef<HTMLDivElement>(null);

  const [tourComplete, setTourComplete] = useState<boolean>(false);

  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { isNewTheme } = useChartTheme();
  const themedPlayerColor = useThemedWistiaPlayerColor();
  // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
  const { name, email, metadata } = loggedInUser;
  const userWatchedIds = useMemo(() => {
    return new Set<string>(
      JSON.parse(
        metadata?.find(
          (item: KeyValueResp | null) =>
            item?.key === MetadataKey.StepsCompleted
        )?.value || '[]'
      )
    );
  }, [metadata]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (userWatchedIds.has('[')) {
      userWatchedIds.clear();
      const updatedStringWatchedIds = JSON.stringify(
        Array.from(userWatchedIds)
      );
      handleUpdateMetadata(loggedInUser, [
        {
          key: MetadataKey.StepsCompleted,
          value: updatedStringWatchedIds,
        },
      ]);
    }
  }, []);

  const { days } = useFreeTrialAlertModalState();

  // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
  const [{ fetching: updatingUserMetaData }, updateUser] =
    useMutation(UpdateUserMutation);

  const mixpanelContactSales = () => {
    mixpanel.track(CTAInteractionTrackingEvents.CONTACT_SALES_CLICK, {
      days_left: days,
    });
  };

  const { mutate: contactSales } = useContactSalesMutation();

  const handleContactSales = () => {
    mixpanelContactSales();

    const contactedSales = localStorage.getItem('contacted-sales') || '';
    if (!contactedSales || !isToday(new Date(contactedSales))) {
      contactSales(
        { email, trialUpgrade: location.pathname },
        {
          onSuccess: () => {
            localStorage.setItem('contacted-sales', new Date().toDateString());
            toastContactSalesRequestSuccess(toast);
          },
          onError: () => {
            toastContactSalesRequestError(toast);
          },
        }
      );
    } else {
      toastContactSalesAlreadyNotified(toast);
    }
  };

  const isVideoOpen = useTourStore((s) => s.isVideoOpen);
  const storeVideoId = useTourStore((s) => s.videoId);
  const isTourComplete = useTourStore((s) => s.isTourComplete);
  const setIsVideoOpen = useTourStore((s) => s.setIsVideoOpen);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);
  const setStoreVideoId = useTourStore((s) => s.setVideoId);

  useEffect(() => {
    if (isVideoOpen) {
      onOpen();
    }
  }, [isVideoOpen, onOpen]);

  useEffect(() => {
    if (storeVideoId) {
      setVideoIdState(storeVideoId);
    }
  }, [storeVideoId]);

  useEffect(() => {
    if (
      isTourComplete ||
      metadata?.find(
        (item: KeyValueResp | null) => item?.key === MetadataKey.TourCompleted
      )?.value === 'true'
    ) {
      setTourComplete(true);
    }
  }, [isTourComplete, metadata]);

  useEffect(() => {
    if (videoId && isOpen) {
      setTimeout(() => {
        const lastCategory = videos[videos.length - 1];
        const isInLastCategory = lastCategory.videos.some(
          // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
          (v: any) => v.id === videoId
        );

        if (isInLastCategory && gridItemRef.current) {
          gridItemRef.current.scrollTo({
            top: gridItemRef.current.scrollHeight,
            behavior: 'instant',
          });
        }
      }, 150);
    }
  }, [videoId, videos, isOpen]);

  const handleVideoProgress = (
    percentWatched: number,
    currentVideoId: string
  ) => {
    if (percentWatched >= 0.1 && !userWatchedIds.has(currentVideoId)) {
      userWatchedIds.add(currentVideoId);

      const updatedStringWatchedIds = JSON.stringify(
        Array.from(userWatchedIds)
      );

      handleUpdateMetadata(loggedInUser, [
        {
          key: MetadataKey.StepsCompleted,
          value: updatedStringWatchedIds,
        },
      ]);

      const currentVideoData = getVideoDataById(currentVideoId);

      mixpanel.track(TutorialTrackingEvents.WATCH_TUTORIAL_VIDEO, {
        video_id: currentVideoId,
        video_name: currentVideoData?.name,
        video_duration: currentVideoData?.duration,
      });
    }
  };

  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  const getVideoDataById = (Id: any) => {
    for (const category of videoMetadata) {
      // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
      const video = category.videos.find((v: any) => v.id === Id);
      if (video) {
        return video;
      }
    }
    console.error('Video not found for Id:', videoId);
    return null;
  };

  const handleUpdateMetadata = (
    loggedInUser: User = {},
    newMeta: KeyValueResp[]
  ) => {
    const metaFieldsToExclude = [MetadataKey.ExpirationDate];

    return new Promise((resolve, reject) => {
      if (isEmpty(loggedInUser)) {
        reject('No logged in user provided');
      }

      const newMetaKeys = newMeta.map((item) => item.key);

      const currentMetaObjects = (loggedInUser.metadata || []).filter(
        (item: KeyValueResp | null) =>
          !newMetaKeys.includes(item?.key) &&
          !metaFieldsToExclude.includes(item?.key as MetadataKey)
      );

      const updatedMeta = [...currentMetaObjects, ...newMeta];

      updateUser({
        id: loggedInUser?.id as string,
        metadata: updatedMeta,
      })
        .then((res) => {
          // update user object on update success
          refetchUserData();
          resolve('User updated');
        })
        .catch((err) => {
          reject('Something went wrong');
        });
    });
  };

  const userTourComplete = useMemo(() => {
    return (
      metadata?.find(
        (item: KeyValueResp | null) => item?.key === MetadataKey.TourCompleted
      )?.value === 'true'
    );
  }, [metadata]);

  const wistiaPlayer = (
    <WistiaPlayer
      mediaId={getVideoDataById(videoId)?.mediaId || ''}
      playerColor={themedPlayerColor}
      autoplay={true}
      onPercentWatchedChange={({ detail: { percentWatched } }) => {
        if (videoId) {
          handleVideoProgress(percentWatched, videoId);
        }
      }}
      onPlay={() => setVideoPlaying(true)}
      onPause={() => setVideoPlaying(false)}
      onEnded={() => {
        const currentVideoName = getVideoDataById(videoId)?.name;

        mixpanel.track(TutorialTrackingEvents.END_TUTORIAL_VIDEO, {
          video_id: videoId,
          video_name: currentVideoName,
        });
      }}
      onApiReady={(event) => {
        event.target.playbackRate = 1.25;
      }}
    />
  );

  if (isNewTheme) {
    const handleOpenSection = (video: VideoData) => {
      const [path] = video.paths;
      onClose();
      if (path) {
        navigate(path);
      }
    };

    return (
      <TutorialModalContent
        isOpen={isOpen}
        onClose={onClose}
        onCloseComplete={() => {
          setIsVideoOpen(false);
          setIsTourComplete(true);
        }}
        closeOnOverlayClick={tourComplete || userTourComplete}
        groups={themedGroups}
        activeVideo={videoId ? getVideoDataById(videoId) : null}
        watchedIds={userWatchedIds}
        onSelectVideo={setStoreVideoId}
        onOpenSection={handleOpenSection}
        player={wistiaPlayer}
        descriptionSuffix={
          videoId === '10' ? (
            <span>
              {` `}
              <UiButton
                variant="link"
                className={modalStyles.contactSalesLink}
                onClick={handleContactSales}
              >
                Contact Sales
              </UiButton>{' '}
              to get started.
            </span>
          ) : undefined
        }
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      onCloseComplete={() => {
        setIsVideoOpen(false);
        setIsTourComplete(true);
      }}
      closeOnOverlayClick={tourComplete || userTourComplete}
      isCentered
    >
      <ModalOverlay bgColor="rgba(0,0,0,0.7)" />
      <ModalContent maxW="1000px">
        <ModalHeader color="text.primary">Tutorials</ModalHeader>
        <ModalCloseButton
          data-testid="tutorial-modal-close-btn"
          color="text.primary"
          _focus={{ boxShadow: 'none' }}
        />
        <ModalBody p="0px 20px 20px 20px">
          <Grid templateColumns="1fr 300px" gap={4} h="100%">
            <GridItem>
              <Box data-testid="tutorial-video-player" w="644px" h="363px">
                {wistiaPlayer}
              </Box>
            </GridItem>
            <GridItem
              ref={gridItemRef}
              bgColor="white"
              overflow="auto"
              position="relative"
            >
              <Accordion
                h="300px"
                allowToggle
                allowMultiple
                defaultIndex={[0, 1, 2]}
              >
                {/** biome-ignore lint/suspicious/noExplicitAny: migrated from develop */}
                {videos.map(({ category, videos }: any, topIndex: any) => (
                  <AccordionItem
                    key={`${category}`}
                    border="none"
                    borderTop={topIndex > 0 ? '1px solid' : 'none'}
                    borderColor="text.50"
                  >
                    <h2>
                      <AccordionButton
                        fontWeight={600}
                        fontSize={14}
                        color="text.primary"
                      >
                        <Box as="span" flex="1" textAlign="left">
                          {category}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel
                      px={1}
                      py={2}
                      borderTop="1px solid"
                      borderColor="text.50"
                    >
                      <VStack alignItems="flex-start" gap={0.5}>
                        {/** biome-ignore lint/suspicious/noExplicitAny: migrated from develop */}
                        {videos.map(({ name, id, duration }: any) => (
                          <Box
                            key={name}
                            px={3}
                            py={2}
                            bgColor={id === videoId ? 'blue.50' : 'white'}
                            w="full"
                            rounded="md"
                            _hover={{
                              bgColor: id === videoId ? 'blue.50' : 'gray.100',
                              opacity: id === videoId ? 0.7 : 1,
                            }}
                            cursor="pointer"
                            onClick={() => setStoreVideoId(id)}
                          >
                            <HStack alignItems="center" gap={1}>
                              <Icon
                                as={
                                  id === videoId && videoPlaying
                                    ? IconPause
                                    : IconPlay
                                }
                                boxSize={5}
                                color={
                                  id === videoId ? 'blue.500' : 'text.primary'
                                }
                              />
                              <Text
                                fontSize={14}
                                color={
                                  id === videoId ? 'blue.500' : 'text.primary'
                                }
                              >
                                {name}
                              </Text>
                              <Spacer />
                              {userWatchedIds.has(id as string) ? (
                                <Icon
                                  as={IconCheckmark1}
                                  color="green.500"
                                  boxSize={5}
                                />
                              ) : (
                                <Text fontSize={10} opacity={0.7}>
                                  {duration}
                                </Text>
                              )}
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </GridItem>
          </Grid>
          <Box pt={3}>
            <Text fontSize="xl" fontWeight={600}>
              {getVideoDataById(videoId)?.name}
            </Text>
            <Text opacity={0.7}>
              {getVideoDataById(videoId)?.description}{' '}
              {videoId === '10' && (
                <span>
                  {` `}
                  <Link
                    onClick={handleContactSales}
                    color="green.500"
                    fontWeight={600}
                  >
                    Contact Sales
                  </Link>{' '}
                  to get started.
                </span>
              )}
            </Text>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default Tutorial;
